import React, { useState, useEffect } from 'react';
import { CreditCard, TrendingUp, Clock, AlertCircle, CheckCircle, Download, Settings, LogOut } from 'lucide-react';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';

interface BillingInfo {
  subscription: {
    status: 'active' | 'trial' | 'cancelled' | 'past_due';
    tier: 'starter' | 'professional' | 'business' | 'enterprise';
    pricePerMonth: number;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    trialEndsAt: string | null;
    minutesIncluded: number;
  };
  usage: {
    minutesUsed: number;
    percentageUsed: number;
    doublesCreated: number;
    doublesLimit: number | null;
  };
  invoices: Array<{
    id: string;
    amount: number;
    status: 'paid' | 'pending' | 'failed';
    issuedAt: string;
    dueDate: string;
    pdfUrl: string;
  }>;
}

const tierColors = {
  starter: 'from-blue-500 to-cyan-500',
  professional: 'from-purple-500 to-pink-500',
  business: 'from-orange-500 to-red-500',
  enterprise: 'from-yellow-500 to-orange-500',
};

const tierNames = {
  starter: 'Starter',
  professional: 'Professional',
  business: 'Business',
  enterprise: 'Enterprise',
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 }
};

const slideUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const Billing: React.FC = () => {
  const { session } = useAuth();
  const [billingData, setBillingData] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'usage' | 'invoices'>('overview');

  useEffect(() => {
    const fetchBillingInfo = async () => {
      if (!session?.user?.id) return;

      try {
        setLoading(true);
        const response = await fetch('/api/billing/info', {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch billing information');
        }

        const data = await response.json();
        setBillingData(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        console.error('Error fetching billing info:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchBillingInfo();
  }, [session]);

  const handleUpgrade = async () => {
    try {
      const response = await fetch('/api/billing/portal', {
        headers: {
          'Authorization': `Bearer ${session?.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to open billing portal');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (err) {
      console.error('Error opening billing portal:', err);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </Layout>
    );
  }

  if (!billingData) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-900">No subscription found</h3>
                <p className="text-sm text-yellow-800 mt-1">
                  {error || 'You need to subscribe to DoubleMe to access this page.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const { subscription, usage, invoices } = billingData;
  const isTrialing = subscription.status === 'trial';
  const daysLeft = isTrialing ? Math.ceil(
    (new Date(subscription.trialEndsAt!).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  ) : null;

  return (
    <Layout>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4"
      >
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div variants={slideUp} className="mb-8">
            <h1 className="text-4xl font-bold text-dark mb-2">Billing & Subscription</h1>
            <p className="text-gray-600">Manage your DoubleMe subscription and view your usage</p>
          </motion.div>

          {/* Current Subscription Card */}
          <motion.div
            variants={slideUp}
            className={`bg-gradient-to-br ${tierColors[subscription.tier]} rounded-xl p-8 text-white mb-8 shadow-lg`}
          >
            <div className="flex justify-between items-start mb-6">
              <div>
                <p className="text-white/80 text-sm mb-1">Current Plan</p>
                <h2 className="text-3xl font-bold">{tierNames[subscription.tier]}</h2>
                <p className="text-white/80 mt-2">${subscription.pricePerMonth}/month</p>
              </div>
              <div className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${
                subscription.status === 'active'
                  ? 'bg-white/20'
                  : subscription.status === 'trial'
                  ? 'bg-green-500/20'
                  : 'bg-red-500/20'
              }`}>
                {subscription.status === 'active' && <CheckCircle className="h-4 w-4" />}
                {subscription.status === 'trial' && <Clock className="h-4 w-4" />}
                {subscription.status === 'cancelled' && <AlertCircle className="h-4 w-4" />}
                <span className="capitalize">{subscription.status}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-white/80 text-sm">Billing Period</p>
                <p className="text-white font-semibold mt-1">
                  {new Date(subscription.currentPeriodStart).toLocaleDateString()} - {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </p>
              </div>
              {isTrialing && (
                <div>
                  <p className="text-white/80 text-sm">Trial Ends In</p>
                  <p className="text-white font-semibold mt-1">{daysLeft} days</p>
                </div>
              )}
              <div>
                <p className="text-white/80 text-sm">Included Minutes</p>
                <p className="text-white font-semibold mt-1">{subscription.minutesIncluded.toLocaleString()}/month</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleUpgrade}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-semibold transition-colors flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                Manage Subscription
              </motion.button>
            </div>
          </motion.div>

          {/* Usage Section */}
          <motion.div variants={slideUp} className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Minutes Usage */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-dark">Minutes Usage</h3>
                <Clock className="h-5 w-5 text-primary" />
              </div>

              <div className="mb-4">
                <div className="flex justify-between items-baseline mb-2">
                  <span className="text-sm text-gray-600">Used</span>
                  <span className="text-lg font-bold text-dark">
                    {usage.minutesUsed.toLocaleString()} / {subscription.minutesIncluded.toLocaleString()}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(usage.percentageUsed, 100)}%` }}
                    transition={{ duration: 1, ease: 'easeOut' }}
                    className={`h-full transition-colors ${
                      usage.percentageUsed > 100
                        ? 'bg-red-500'
                        : usage.percentageUsed > 80
                        ? 'bg-yellow-500'
                        : 'bg-primary'
                    }`}
                  />
                </div>
              </div>

              <p className="text-sm text-gray-600">
                {usage.percentageUsed > 100
                  ? `${(usage.percentageUsed - 100).toFixed(1)}% over limit`
                  : `${(100 - usage.percentageUsed).toFixed(1)}% remaining`}
              </p>

              {usage.percentageUsed > 80 && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    You're using {usage.percentageUsed.toFixed(0)}% of your minutes. Consider upgrading to a higher tier.
                  </p>
                </div>
              )}
            </div>

            {/* Doubles Usage */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-dark">Doubles Created</h3>
                <TrendingUp className="h-5 w-5 text-primary" />
              </div>

              <div className="mb-4">
                <div className="flex justify-between items-baseline mb-2">
                  <span className="text-sm text-gray-600">Created</span>
                  <span className="text-lg font-bold text-dark">
                    {usage.doublesCreated} {usage.doublesLimit ? `/ ${usage.doublesLimit}` : '/ Unlimited'}
                  </span>
                </div>
                {usage.doublesLimit && (
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min((usage.doublesCreated / usage.doublesLimit) * 100, 100)}%` }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                      className="h-full bg-primary"
                    />
                  </div>
                )}
              </div>

              <p className="text-sm text-gray-600">
                {usage.doublesLimit
                  ? `${usage.doublesLimit - usage.doublesCreated} slots available`
                  : 'Unlimited doubles available'}
              </p>
            </div>
          </motion.div>

          {/* Tabs */}
          <motion.div variants={slideUp} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Tab Headers */}
            <div className="flex border-b border-gray-200">
              {['overview', 'usage', 'invoices'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab as any)}
                  className={`flex-1 py-4 px-6 font-semibold capitalize transition-colors ${
                    activeTab === tab
                      ? 'bg-primary/5 text-primary border-b-2 border-primary'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'overview' && (
                <motion.div variants={stagger} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-2">Plan Tier</p>
                      <p className="text-lg font-semibold text-dark capitalize">{subscription.tier}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-2">Monthly Price</p>
                      <p className="text-lg font-semibold text-dark">${subscription.pricePerMonth}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-2">Subscription Status</p>
                      <p className="text-lg font-semibold text-dark capitalize">{subscription.status}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600 mb-2">Next Billing Date</p>
                      <p className="text-lg font-semibold text-dark">
                        {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div className="pt-4">
                    <button
                      onClick={handleUpgrade}
                      className="text-primary font-semibold hover:underline flex items-center gap-2"
                    >
                      <Settings className="h-4 w-4" />
                      Change subscription in billing portal
                    </button>
                  </div>
                </motion.div>
              )}

              {activeTab === 'usage' && (
                <motion.div variants={stagger} className="space-y-4">
                  <p className="text-gray-600 mb-6">
                    This is your usage for the current billing period ({new Date(subscription.currentPeriodStart).toLocaleDateString()} - {new Date(subscription.currentPeriodEnd).toLocaleDateString()})
                  </p>

                  <div className="space-y-4">
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <p className="font-semibold text-dark">Minutes Used</p>
                        <p className="text-lg font-bold text-primary">{usage.minutesUsed.toLocaleString()}</p>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-primary"
                          style={{ width: `${Math.min(usage.percentageUsed, 100)}%` }}
                        />
                      </div>
                    </div>

                    <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex justify-between items-center">
                        <p className="font-semibold text-dark">Doubles Created</p>
                        <p className="text-lg font-bold text-purple-600">{usage.doublesCreated}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'invoices' && (
                <motion.div variants={stagger} className="space-y-4">
                  {invoices && invoices.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Date</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Amount</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((invoice) => (
                            <motion.tr
                              key={invoice.id}
                              variants={slideUp}
                              className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                            >
                              <td className="py-3 px-4 text-sm text-gray-600">
                                {new Date(invoice.issuedAt).toLocaleDateString()}
                              </td>
                              <td className="py-3 px-4 text-sm font-semibold text-dark">
                                ${(invoice.amount / 100).toFixed(2)}
                              </td>
                              <td className="py-3 px-4 text-sm">
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                  invoice.status === 'paid'
                                    ? 'bg-green-100 text-green-700'
                                    : invoice.status === 'pending'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-red-100 text-red-700'
                                }`}>
                                  {invoice.status}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-right">
                                <a
                                  href={invoice.pdfUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline flex items-center gap-1 justify-end"
                                >
                                  <Download className="h-4 w-4" />
                                  Download
                                </a>
                              </td>
                            </motion.tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-600 text-center py-8">No invoices yet</p>
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </Layout>
  );
};

export default Billing;
